import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Inventory } from "@/components/Inventory";
import { AIRecommender } from "@/components/AIRecommender";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { QuickView } from "@/components/QuickView";
import { CompareBar } from "@/components/CompareBar";
import { CompareModal } from "@/components/CompareModal";
import { SiteReviews } from "@/components/SiteReviews";
import { FeaturedReviewsCarousel } from "@/components/FeaturedReviewsCarousel";
import api from "@/lib/api";
import { setPageMeta, siteOrigin } from "@/lib/seo";
import { useSiteContent } from "@/context/SiteContentContext";
import { countyParamFromList, parseCountyParam } from "@/lib/carCountyCounts";
import { computeInventoryStats } from "@/lib/carCounts";
import { Loader2 } from "lucide-react";

export default function Home() {
  const { content } = useSiteContent();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMake = searchParams.get("marca") || searchParams.get("make") || "all";
  const [selectedCounties, setSelectedCounties] = useState(() =>
    parseCountyParam(searchParams.get("megye") || searchParams.get("county")),
  );
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quickCar, setQuickCar] = useState(null);
  const [quickOpen, setQuickOpen] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [prefillCar, setPrefillCar] = useState(null);

  useEffect(() => {
    const seo = content.seo || {};
    setPageMeta({ description: seo.default_description, url: siteOrigin() });
    if (seo.default_title) document.title = seo.default_title;
  }, [content.seo?.default_title, content.seo?.default_description]);

  useEffect(() => {
    setSelectedCounties(parseCountyParam(searchParams.get("megye") || searchParams.get("county")));
  }, [searchParams]);

  const loadCars = useCallback((showSpinner = false) => {
    if (showSpinner) setLoading(true);
    api
      .get("/cars")
      .then((res) => setCars(res.data))
      .catch(() => setCars([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadCars(true);
  }, [loadCars]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") loadCars(false);
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadCars]);

  const handleCountiesChange = (next) => {
    setSelectedCounties(next);
    const params = new URLSearchParams(searchParams);
    if (next.length) params.set("megye", countyParamFromList(next));
    else params.delete("megye");
    setSearchParams(params, { replace: true });
  };

  const openQuickView = (car) => {
    setQuickCar(car);
    setQuickOpen(true);
  };

  const toggleCompare = (car) => {
    setCompareList((prev) => {
      if (prev.some((c) => c.id === car.id)) return prev.filter((c) => c.id !== car.id);
      if (prev.length >= 4) return prev;
      return [...prev, car];
    });
  };

  const heroStats = useMemo(() => {
    if (loading) return { loading: true };
    return { loading: false, ...computeInventoryStats(cars) };
  }, [cars, loading]);

  return (
    <div className="dark bg-anthracite-900 min-h-screen text-white">
      <Navbar />
      <Hero stats={heroStats} />

      {(content.home_sections || [])
        .filter((s) => s.enabled)
        .map((sec) => {
          if (sec.id === "featured_reviews") {
            return <FeaturedReviewsCarousel key={sec.id} />;
          }
          if (sec.id === "inventory") {
            return loading ? (
              <div key={sec.id} className="flex items-center justify-center py-32">
                <Loader2 className="w-8 h-8 text-electric animate-spin" />
              </div>
            ) : (
              <Inventory
                key={sec.id}
                cars={cars}
                initialMake={initialMake}
                selectedCounties={selectedCounties}
                onCountiesChange={handleCountiesChange}
                onQuickView={openQuickView}
                compareList={compareList}
                onToggleCompare={toggleCompare}
              />
            );
          }
          if (sec.id === "ai_recommender") {
            return <AIRecommender key={sec.id} onQuickView={openQuickView} />;
          }
          if (sec.id === "site_reviews") {
            return <SiteReviews key={sec.id} />;
          }
          if (sec.id === "contact") {
            return (
              <Contact
                key={sec.id}
                prefillCar={prefillCar}
                onPrefillConsumed={() => setPrefillCar(null)}
              />
            );
          }
          return null;
        })}

      <Footer />

      <QuickView car={quickCar} open={quickOpen} onClose={() => setQuickOpen(false)} />

      <CompareBar
        compareList={compareList}
        onClear={() => setCompareList([])}
        onRemove={toggleCompare}
        onOpen={() => setCompareOpen(true)}
      />
      <CompareModal open={compareOpen} onClose={() => setCompareOpen(false)} cars={compareList} />
    </div>
  );
}
